import { useEffect, useMemo, useState } from 'react';
import { FolderPlus, LayoutGrid, List, Search, Upload } from 'lucide-react';
import type { JobFileEntry, JobFileListQuery, JobFolder } from '@jobos/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useJobFileUpload, useJobFiles, useMoveJobFiles } from '@/hooks/useJobFiles';
import { useJobFolders } from '@/hooks/useJobFolders';
import { ToastAction } from '@/components/ui/toast';
import { canSharePhotos, sharePhotos } from '@/lib/sharePhotos';
import { isImageEntry, jobFilePreviewKind } from './jobFileDisplay';
import { JobFileDeleteDialog } from './JobFileDeleteDialog';
import { JobFileDocumentViewer } from './JobFileDocumentViewer';
import { JobFileEditDialog } from './JobFileEditDialog';
import { JobFileLightbox } from './JobFileLightbox';
import { JobFileCaptureBar, type JobFileCaptureSource } from './JobFileCaptureBar';
import { JobFileDndProvider } from './JobFileDndProvider';
import { JobFileDropArea } from './JobFileDropArea';
import { JobFileSelectionBar } from './JobFileSelectionBar';
import { JobFileShareDialog } from './JobFileShareDialog';
import { JobFilesGrid } from './JobFilesGrid';
import { JobFilesList } from './JobFilesList';
import { JobFileUploadDialog } from './JobFileUploadDialog';
import { JobFileUploadProgress } from './JobFileUploadProgress';
import { JobFileUploadProgressBar } from './JobFileUploadProgressBar';
import { JobFileMoveDialog } from './JobFileMoveDialog';
import { JobFolderNavigator } from './JobFolderNavigator';
import {
  JobFolderCreateDialog,
  JobFolderDeleteDialog,
  JobFolderRenameDialog,
} from './JobFolderDialogs';
import { useAcceptedJobFiles } from './useAcceptedJobFiles';
import { useJobFileSelection } from './useJobFileSelection';
import type { DropResolution } from './jobFileDrag';
import { describeMoveResult } from './jobFileMoveResult';

// The job file store's container: the folder navigator, the search box and the
// view area. Built mobile-first — every control wraps rather than overflowing at
// 390px, because a phone is where photos get taken and looked at.

const SEARCH_DEBOUNCE_MS = 300;
/** How long the save-to-phone offer stays up — the 5s default is a blink here. */
const SAVE_OFFER_MS = 10_000;

/** Where the grid-vs-list preference lives between visits. */
export const JOB_FILES_VIEW_KEY = 'jobFiles.view';

type ViewMode = 'grid' | 'list';

function readStoredView(): ViewMode {
  try {
    return localStorage.getItem(JOB_FILES_VIEW_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    // Private-mode Safari throws on access rather than returning null.
    return 'grid';
  }
}

export function JobFilesSection({ jobId }: { jobId: string }) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // The open folder; null is the job root. Drives both the navigator and the
  // file query, so the list can never show a folder the breadcrumb doesn't.
  const [folderId, setFolderId] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(readStoredView);
  const [showUpload, setShowUpload] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState<JobFolder | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<JobFolder | null>(null);
  // The photos the viewer pages through, captured at open: the current filtered
  // set as the active view orders it, minus everything that isn't an image.
  const [viewer, setViewer] = useState<{ files: JobFileEntry[]; index: number } | null>(null);
  // Documents don't page: there is no roll to move through, just the one file.
  // Not named `document` — shadowing the global in a component file invites the
  // kind of bug that only shows up the day someone reaches for the real one.
  const [documentFile, setDocumentFile] = useState<JobFileEntry | null>(null);
  // The file whose metadata is being edited; null keeps the dialog closed.
  const [editing, setEditing] = useState<JobFileEntry | null>(null);
  // Likewise for the files being moved into a folder — one from a card's menu,
  // or the whole selection from the selection bar.
  const [moving, setMoving] = useState<JobFileEntry[] | null>(null);
  // Likewise for the files a share link is being made from. An empty array is
  // never set: the Share button only exists while something is selected.
  const [sharing, setSharing] = useState<JobFileEntry[] | null>(null);
  // Likewise for the file being deleted. The row leaves the list the moment the
  // delete is confirmed, so this holds the entry rather than an id.
  const [deleting, setDeleting] = useState<JobFileEntry | null>(null);

  const { items, enqueue, retry, retryAllFailed, clearSettled, isUploading } =
    useJobFileUpload(jobId);
  const acceptFiles = useAcceptedJobFiles();
  const { data: folders } = useJobFolders(jobId);
  const { mutateAsync: moveFiles } = useMoveJobFiles(jobId);
  const { toast } = useToast();

  const openFolder = folders?.find((folder) => folder.id === folderId) ?? null;

  /**
   * Hand the shots back to the phone. Offered rather than done, because the OS
   * share sheet is the only way onto the camera roll and it opens from a tap of
   * ours — never from the file input's own change event.
   */
  const offerToSaveToPhone = (photos: File[]) => {
    toast({
      title: photos.length === 1 ? 'Photo added to job' : `${photos.length} photos added to job`,
      description: 'A photo taken here is not on your phone. Save a copy?',
      // Asking for a decision, on a white page, on a phone the person is holding
      // at arm's length: the default white card does not carry.
      variant: 'prominent',
      // Longer than the default: the person is watching the camera close, not us.
      duration: SAVE_OFFER_MS,
      action: (
        <ToastAction
          altText="Save to phone"
          onClick={() => {
            void sharePhotos(photos).then((outcome) => {
              // 'dismissed' is the sheet closed on purpose, which needs no reply.
              if (outcome === 'failed') {
                toast({
                  title: "Couldn't save to phone",
                  description: 'Your phone turned down the share. The photo is still on the job.',
                  variant: 'destructive',
                });
              }
            });
          }}
        >
          Save
        </ToastAction>
      ),
    });
  };

  /**
   * The camera and library entry points. Both land in whatever folder is open,
   * which is the only place a user would expect them: the destination is on
   * screen when the shutter is pressed.
   *
   * A camera shot exists only in this page until it is uploaded — the OS keeps
   * no copy of a photo taken through a file input — so it, and only it, gets the
   * offer to save. A library pick is already in the roll it came from.
   */
  const handleCapture = (files: File[], source: JobFileCaptureSource) => {
    const accepted = acceptFiles(files);
    if (accepted.length === 0) return;
    enqueue(accepted, { folderId });
    if (source === 'camera' && canSharePhotos(accepted)) offerToSaveToPhone(accepted);
  };

  /**
   * Files dragged in from the desktop. The same filtering the dialog and the
   * capture bar use — the queue never sees a file the library would refuse.
   *
   * `target` is where they land: the open folder for a drop on the file area,
   * or the folder whose tile was dropped on. A drop on a tile is the only way
   * to upload into a folder without opening it first.
   */
  const handleExternalDrop = (dropped: File[], target: string | null) => {
    const accepted = acceptFiles(dropped);
    if (accepted.length === 0) return;
    enqueue(accepted, { folderId: target });
  };

  // Successes have nothing left to report once the queue drains; failures and
  // in-flight originals survive this, which is what keeps a retry reachable.
  useEffect(() => {
    if (!isUploading) clearSettled();
  }, [isUploading, clearSettled]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    try {
      localStorage.setItem(JOB_FILES_VIEW_KEY, view);
    } catch {
      // A preference that can't be stored is not worth failing a render over.
    }
  }, [view]);

  // Only the active filters go in, so the query key stays stable — an inactive
  // filter must not read as a different list than no filter at all.
  const filters = useMemo<JobFileListQuery>(() => {
    const next: JobFileListQuery = {};
    const term = debouncedSearch.trim();
    // A search spans the whole job — a term is a question about the job, not
    // about the folder that happens to be open.
    if (term) next.q = term;
    else next.folderId = folderId;
    return next;
  }, [folderId, debouncedSearch]);

  const searching = debouncedSearch.trim().length > 0;

  const { data: files, isLoading, error, refetch } = useJobFiles(jobId, filters);

  // The selection belongs to the list on screen, so its scope is whatever
  // defines that list: the open folder, and the search that overrides it.
  const selection = useJobFileSelection(`${folderId ?? 'root'}|${debouncedSearch.trim()}`);

  const folderName = (target: string | null): string =>
    target === null ? 'All files' : (folders?.find((f) => f.id === target)?.name ?? 'the folder');

  const handleDrop = async ({ ids, folderId: target }: DropResolution) => {
    const result = await moveFiles({ ids, folderId: target });

    const message = describeMoveResult(result, folderName(target));
    if (message) {
      toast({
        title: 'Move files',
        description: message,
        variant: result.moved.length > 0 ? 'default' : 'destructive',
      });
    }

    // What stayed behind stays ticked, so which files did not go is something
    // the user can see rather than work out from the toast.
    selection.replace(result.locked);
  };

  /**
   * Open the right viewer for the file.
   *
   * The lightbox renders an `<img>` and can only page through images, so it gets
   * the image subset of the ordered set. Everything else — PDFs and the files
   * that can't be previewed at all — goes to the document viewer on its own.
   */
  const handleView = (file: JobFileEntry, ordered: JobFileEntry[]) => {
    if (jobFilePreviewKind(file) !== 'image') {
      setDocumentFile(file);
      return;
    }
    const photos = ordered.filter(isImageEntry);
    const index = photos.findIndex((entry) => entry.id === file.id);
    if (index === -1) return;
    setViewer({ files: photos, index });
  };

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
  };

  const count = files?.length ?? 0;

  return (
    // The bottom padding clears the fixed capture bar on a phone, so the last row
    // of the grid can still be tapped instead of hiding behind it.
    <div className="space-y-4 pb-24 pt-2 sm:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Files</h2>
        {!isLoading && !error && (
          <span className="text-sm text-gray-500">
            {count} {count === 1 ? 'file' : 'files'}
            {searching ? ' match' : ''}
          </span>
        )}
      </div>

      {/* Wraps rather than scrolls: at 390px the search takes a full row and the
          chip rows fall underneath it. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files..."
            aria-label="Search files"
            className="pl-8"
          />
        </div>

        <div className="flex items-center gap-1 rounded-md border border-gray-300 p-0.5">
          <button
            type="button"
            aria-label="Grid view"
            aria-pressed={view === 'grid'}
            onClick={() => setView('grid')}
            className={`rounded p-1.5 ${view === 'grid' ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="List view"
            aria-pressed={view === 'list'}
            onClick={() => setView('list')}
            className={`rounded p-1.5 ${view === 'list' ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-gray-50'}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>

        <Button onClick={() => setShowUpload(true)}>
          <Upload className="h-4 w-4" />
          Upload
        </Button>

        {/* Inside a root folder this makes a subfolder. Hidden one level down —
            two levels is the cap the service enforces — and inside the
            app-owned Expenses folder, which stays flat. */}
        {(folderId === null ||
          (openFolder !== null &&
            openFolder.parentFolderId === null &&
            openFolder.slug === null)) && (
          <Button variant="outline" onClick={() => setCreatingFolder(true)}>
            <FolderPlus className="h-4 w-4" />
            {folderId === null ? 'New folder' : 'New subfolder'}
          </Button>
        )}
      </div>

      {/* A DndContext renders no element of its own, so the navigator, the
          selection bar and the file area stay direct children of the section and
          keep its vertical rhythm. They have to share one context: a droppable
          and the draggables aimed at it cannot live in separate ones. */}
      <JobFileDndProvider
        jobId={jobId}
        files={files ?? []}
        selectedIds={selection.ids}
        onSelectionChange={selection.replace}
        onDrop={handleDrop}
      >
        {/* Hidden while searching: a search spans the job, so a folder row above
            the results would suggest the results are scoped to one. */}
        {!searching && (
          <JobFolderNavigator
            jobId={jobId}
            folderId={folderId}
            onNavigate={setFolderId}
            fileCount={files?.length ?? 0}
            onRename={setRenamingFolder}
            onDelete={setDeletingFolder}
            onFilesDropped={handleExternalDrop}
          />
        )}

        {selection.ids.size > 0 && (
          <JobFileSelectionBar
            count={selection.ids.size}
            onMove={() => setMoving((files ?? []).filter((file) => selection.has(file.id)))}
            onShare={() => setSharing((files ?? []).filter((file) => selection.has(file.id)))}
            onClear={selection.clear}
          />
        )}

        {/* The queue lives on the page, not in the dialog: the dialog closes the
            moment files are accepted, and a batch has to stay watchable after it
            does. Desktop only — the phone gets the fixed dock at the bottom
            instead, so a batch stays in sight through a scroll rather than at the
            top of it. */}
        {items.length > 0 && (
          <div className="hidden sm:block">
            <JobFileUploadProgress items={items} onRetry={retry} onRetryAll={retryAllFailed} />
          </div>
        )}

        {/* The file area takes files dragged in from the desktop, landing them
            in the open folder. The empty states are inside it on purpose: an
            empty folder is exactly where a drop is most likely, and a folder
            with nothing in it still needs something to aim at. */}
        <JobFileDropArea
          onFiles={(dropped) => handleExternalDrop(dropped, folderId)}
          destination={folderName(folderId)}
          disabled={isLoading || Boolean(error)}
          className={count === 0 ? 'min-h-32' : ''}
        >
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading files…</p>
          ) : error ? (
            <div className="rounded border border-red-200 bg-red-50 p-6 text-center">
              <p className="text-sm text-red-700">Couldn't load files.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                Try again
              </Button>
            </div>
          ) : count === 0 ? (
            // A folder's own emptiness is the navigator's line to say, so this only
            // covers the search and the job with nothing in it at all.
            searching ? (
              <div className="rounded border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                <p>No files match that search.</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={clearFilters}>
                  Clear search
                </Button>
              </div>
            ) : folderId === null ? (
              <div className="rounded border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
                <p>No files yet. Photos, receipts and documents for this job will show up here.</p>
                <p className="mt-1">Drag files in from your desktop, or use Upload.</p>
              </div>
            ) : null
          ) : view === 'grid' ? (
            <JobFilesGrid
              jobId={jobId}
              files={files ?? []}
              selectedIds={selection.ids}
              onToggleSelect={selection.toggle}
              onView={handleView}
              onEdit={setEditing}
              onMove={(file) => setMoving([file])}
              onDelete={setDeleting}
            />
          ) : (
            <JobFilesList
              jobId={jobId}
              files={files ?? []}
              selectedIds={selection.ids}
              onToggleSelect={selection.toggle}
              onView={handleView}
              onEdit={setEditing}
              onMove={(file) => setMoving([file])}
              onDelete={setDeleting}
            />
          )}
        </JobFileDropArea>
      </JobFileDndProvider>

      {viewer && (
        <JobFileLightbox
          jobId={jobId}
          files={viewer.files}
          index={viewer.index}
          onIndexChange={(index) => setViewer((open) => (open ? { ...open, index } : open))}
          onClose={() => setViewer(null)}
        />
      )}

      <JobFileDocumentViewer
        jobId={jobId}
        file={documentFile}
        onClose={() => setDocumentFile(null)}
      />

      <JobFileEditDialog jobId={jobId} file={editing} onClose={() => setEditing(null)} />

      <JobFileUploadDialog
        open={showUpload}
        onClose={() => setShowUpload(false)}
        onFiles={(dropped) => enqueue(dropped, { folderId })}
        {...(openFolder?.name ? { folderName: openFolder.name } : {})}
      />

      <JobFileDeleteDialog jobId={jobId} file={deleting} onClose={() => setDeleting(null)} />

      <JobFileMoveDialog jobId={jobId} files={moving} onClose={() => setMoving(null)} />

      <JobFileShareDialog jobId={jobId} files={sharing} onClose={() => setSharing(null)} />

      <JobFolderCreateDialog
        jobId={jobId}
        open={creatingFolder}
        onClose={() => setCreatingFolder(false)}
        {...(folderId ? { parentFolderId: folderId } : {})}
        {...(openFolder?.name ? { parentName: openFolder.name } : {})}
      />

      <JobFolderRenameDialog
        jobId={jobId}
        folder={renamingFolder}
        onClose={() => setRenamingFolder(null)}
      />

      <JobFolderDeleteDialog
        jobId={jobId}
        folder={deletingFolder}
        {...(deletingFolder?.parentFolderId
          ? {
              parentName:
                folders?.find((f) => f.id === deletingFolder.parentFolderId)?.name ?? 'All files',
            }
          : {})}
        onClose={() => {
          // A deleted folder can't stay open. Land where its files went: the
          // parent folder, or the job root when it was a root folder.
          if (deletingFolder && deletingFolder.id === folderId) {
            setFolderId(deletingFolder.parentFolderId);
          }
          setDeletingFolder(null);
        }}
      />

      <JobFileUploadProgressBar items={items} onRetry={retry} onRetryAll={retryAllFailed} />
      <JobFileCaptureBar onFiles={handleCapture} />
    </div>
  );
}
